import "../../side.css";
import { Button, Nav } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import OKOCoreLogo from "../../../assets/OKO_Core_Logotype2.svg";
import { colors, constants } from "utils";
import { useEffect, useState } from "react";
import { SettingsModal } from "./SignUp/SettingsModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "hooks/useFirebase";
import { UserProfileCard } from "../../shared/UserProfileCard";
import { useEducatorAccount } from "hooks/useEducatorAccount/useEducatorAccount";
import { TeacherSelector } from "./TeacherSelector";
import { SuperuserPreviewLine } from "./SuperuserPreviewLine";
import { useImpersonation } from "hooks/useImpersonation/useImpersonation";
import { useSchoolReportScope } from "./SchoolReports/useSchoolReportScope";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { showsStudentReports } from "components/pages/Educator/StudentReports/studentReportsNavGate";
import { trackClickedButton } from "utils/analytics";
import { matchPageName } from "hooks/usePageViewTracker";
import { initialTab } from "./Session/sessionReportTabs";
import { useLiveSessions } from "hooks/useLiveSessionsStream/LiveSessionsProvider";
import { ReactComponent as GroupIcon } from "assets/nav/group.svg";
import { ReactComponent as MessagesSquareIcon } from "assets/nav/messages-square.svg";
import { ReactComponent as RadioIcon } from "assets/nav/radio.svg";
import { ReactComponent as BookOpenTextIcon } from "assets/nav/book-open-text.svg";
import { ReactComponent as CalendarIcon } from "assets/nav/calendar.svg";
import { ReactComponent as SchoolIcon } from "assets/nav/school.svg";
import { ReactComponent as UsersRoundIcon } from "assets/nav/users-round.svg";
import { ReactComponent as UserRoundIcon } from "assets/nav/user-round.svg";
import { ReactComponent as FolderOpenIcon } from "assets/nav/folder-open.svg";

// Every button_name the nav can emit. trackNavClick receives the label as a
// variable, so this list is what lets the tracked-values inventory be checked
// by reading the code.
export const NAV_BUTTON_NAMES = [
  constants.MANAGE_GROUPS,
  constants.LIVE_SESSIONS,
  constants.BY_SESSION,
  constants.BY_LESSON,
  constants.BY_DATE,
  constants.SCHOOL_REPORTS,
  constants.DISTRICT_REPORTS,
  constants.CLASS_REPORTS,
  constants.STUDENT_REPORTS,
  constants.RESOURCE_HUB,
  constants.SETTINGS,
];

const NavItem = ({ to, active, Icon, onClick, className = "", children }) => (
  <Nav.Item>
    <Nav.Link
      as={Link}
      to={to}
      onClick={onClick}
      className={"side-nav-item" + (active ? " side-nav-item-active" : "") + (className ? ` ${className}` : "")}
      aria-current={active ? "page" : undefined}
    >
      <Icon className="side-nav-icon" aria-hidden="true" focusable="false" />
      <span className="side-nav-label">{children}</span>
    </Nav.Link>
  </Nav.Item>
);

export const EducatorSide = ({ showCloseButton, toggleSidebar, isEducatorOnboarding }) => {
  const { impersonatedUser } = useImpersonation();
  const { scope: reportScope } = useSchoolReportScope();
  const lessonReportsEnabled = useFeatureFlag("lessonReport");
  // While the feed holds any session, the Live Sessions item turns green and
  // its icon pulses; it reverts once the last session_ended empties the map. A feed
  // that has been down past its grace period can't vouch for its sessions, so
  // it shows the plain item. The server switching live sessions off hides it.
  const { sessions: liveSessions, status: liveStatus, outage: liveOutage } = useLiveSessions();
  const hasLiveSession = !liveOutage && Object.keys(liveSessions || {}).length > 0;
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const location = useLocation();
  // Fires alongside the Link rather than in place of it, so tracking can never
  // block navigation. page_name is the page being left.
  const trackNavClick = (label) =>
    trackClickedButton(label, { source: "Left Nav", page_name: matchPageName(location.pathname) });
  const [user, setUser] = useState(null);
  // Read from the shared account cache rather than fetched per mount: every
  // educator route renders its own sidebar, so a per-mount fetch would hide
  // the school-gated item on each navigation until the answer came back.
  const { activeSchool } = useEducatorAccount();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);
  const showStudentReports = showsStudentReports(activeSchool);

  // By Session and By Lesson share a path and differ by ?tab=, so the active
  // row is decided by the same reader the page uses. Without the Lessons
  // flag the page only ever shows Groups, whatever the URL says.
  const sessionTab = lessonReportsEnabled ? initialTab(location.search) : "groups";
  const isActive = (item) =>
    location.pathname === item.path && (!item.tab || item.tab === sessionTab);
  const href = (item) => (item.tab ? `${item.path}?tab=${item.tab}` : item.path);

  const sections = [
    {
      header: constants.SESSION_REPORTS_SECTION,
      items: [
        {
          label: constants.BY_SESSION,
          path: constants.SESSION_HISTORY_PATH,
          tab: "groups",
          Icon: MessagesSquareIcon,
        },
        lessonReportsEnabled && { label: constants.BY_LESSON, path: constants.SESSION_HISTORY_PATH, tab: "lessons", Icon: BookOpenTextIcon },
        { label: constants.BY_DATE, path: constants.WEEKLY_REPORTS_PATH, Icon: CalendarIcon },
      ],
    },
    {
      header: constants.DATA_REPORTS_SECTION,
      items: [
        reportScope && {
          label: reportScope.role === "district_admin" ? constants.DISTRICT_REPORTS : constants.SCHOOL_REPORTS,
          path: constants.SCHOOL_REPORTS_PATH,
          Icon: SchoolIcon,
        },
        { label: constants.CLASS_REPORTS, path: constants.CLASS_REPORTS_PATH, Icon: UsersRoundIcon },
        showStudentReports && { label: constants.STUDENT_REPORTS, path: constants.STUDENT_REPORTS_PATH, Icon: UserRoundIcon },
      ],
    },
    {
      header: constants.SUPPORT_SECTION,
      items: [{ label: constants.RESOURCE_HUB, path: constants.RESOURCE_HUB_PATH, Icon: FolderOpenIcon }],
    },
  ];

  return (
    <div id="sidebar-toggle" style={{
      background: impersonatedUser ? colors.pink50 : "#FFFFFF",
      borderRight: `2px solid ${colors.nightlyWoods500}`,
      bottom: 0,
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      overflow: "hidden",
      position: "fixed",
      top: 0,
      width: "312px" }}>
      {showCloseButton && <Button onClick={toggleSidebar}
                                  aria-label="Close sidebar"
                                  style={{
                                  backgroundColor: colors.nightlyWoods500,
                                  border: "none",
                                  position: "absolute",
                                  left: "262px",
                                  top: "8px" }}>
        <FontAwesomeIcon icon={faClose} />
      </Button>}
      <div style={{ flexShrink: 0 }}>
        <div style={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          padding: "40px 32px 16px 32px",
          height: "100px" }}>
          <img src={OKOCoreLogo} alt="OKO logo" />
        </div>
        <div style={{ padding: "24px 0 0 0" }}>
          <TeacherSelector currentUserEmail={user?.email} />
          <SuperuserPreviewLine />
        </div>
      </div>
      <nav className="side-nav" aria-label="Main">
        <Nav>
          <NavItem
            to={constants.EDUCATOR_PATH}
            active={location.pathname === constants.EDUCATOR_PATH}
            Icon={GroupIcon}
            onClick={() => trackNavClick(constants.MANAGE_GROUPS)}
          >
            {constants.MANAGE_GROUPS}
          </NavItem>
          {liveStatus !== "disabled" && (
          <NavItem
            to={constants.LIVE_SESSIONS_PATH}
            active={location.pathname === constants.LIVE_SESSIONS_PATH}
            Icon={RadioIcon}
            className={hasLiveSession ? "side-nav-item-live" : ""}
            onClick={() => trackNavClick(constants.LIVE_SESSIONS)}
          >
            {hasLiveSession ? (
              <>
                <span className="side-nav-live-label">{constants.LIVE_SESSIONS}</span>
                <span className="visually-hidden">, sessions running now</span>
              </>
            ) : (
              constants.LIVE_SESSIONS
            )}
          </NavItem>
          )}
        </Nav>
        {sections.map((section) => (
          <div className="side-nav-section" key={section.header}>
            <div className="side-nav-section-header">{section.header}</div>
            <Nav>
              {section.items.filter(Boolean).map((item) => (
                <NavItem
                  key={item.label}
                  to={href(item)}
                  active={isActive(item)}
                  Icon={item.Icon}
                  onClick={() => trackNavClick(item.label)}
                >
                  {item.label}
                </NavItem>
              ))}
            </Nav>
          </div>
        ))}
      </nav>
      <UserProfileCard
        user={user}
        activeSchool={activeSchool}
        onOpenSettings={() => {
          trackNavClick(constants.SETTINGS);
          setIsSettingsModalOpen(true);
        }}
      />
      {isSettingsModalOpen &&
        <SettingsModal isOpen={isSettingsModalOpen}
                       isOnboarding={isEducatorOnboarding}
                       setIsOpen={setIsSettingsModalOpen} />}
    </div>
  );
};
